<template>
  <b-container>
    <div class="new-password tw:py-6 tw:px-5 tw:bg-white">
      <h1 v-if="!isRequestSend" class="new-password__title">New Password</h1>
      <h1 v-else class="new-password__title">Success!</h1>
      <form v-if="!isRequestSend" @submit.prevent="handleForm" class="new-password__form">
        <img
          class="new-password__toggle-show-password icon"
          src="../../assets/images/myScans/icon_action_hide.svg"
          id="passwordShow"
          @click="toggleShowPassword"
          alt=""
        />
        <img
          class="new-password__toggle-show-password new-password__toggle-show-password--bigger icon"
          src="../../assets/images/myScans/icon_action_show.svg"
          id="passwordHide"
          @click="toggleShowPassword"
          alt=""
        />
        <input
          class="input new-password_input"
          name="password"
          id="password"
          type="password"
          @blur="activeInputName = null"
          @input="checkKeyEvent"
          v-model.trim="inputData.password"
        />
        <label
          for="password"
          class="label"
          v-bind:class="{
            activeInput: inputData.password !== isEmptyInput || activeInputName === 'password',
          }"
          >Password</label
        >
        <span id="passwordError" class="new-password__error new-password__error-length">
          <span class="new-password__error-text"> Required field </span>
        </span>
        <span id="passwordLengthError" class="new-password__error new-password__error-length">
          <span class="new-password__error-text"> Minimum 12 characters </span>
        </span>
        <AuthActionButton
          full-width
          variant="primary"
          class="new-password__btn-submit"
          :show-icon="false"
          button-type="submit"
          text="Confirm New Password"
        />
      </form>
      <div v-else class="new-password__message">
        <h6 class="new-password__text new-password__text--center">
          You have set a new password. <br />
          Login to start
        </h6>
        <b-nav-item to="/sign-in" class="new-password__back-link">Back to Login</b-nav-item>
      </div>
      <p v-if="ssoOnlyMessage" class="new-password__sso-message">{{ ssoOnlyMessage }}</p>
      <div v-if="isPasswordChangingNow" class="new-password__spinner">
        <Spinner />
      </div>
    </div>
  </b-container>
</template>

<script>
import {
  checkFormInputsForCompleteness,
  checkFormInputsForErrors,
  checkPasswordLength,
  toggleShowPassword,
  validatePassword,
  raiseError,
  hideErrors,
  ERROR_CODES,
} from '@/services/functions';
import Spinner from '@/components/Spinner';
import AuthActionButton from '@/_core/shared/ui/AuthActionButton/AuthActionButton.vue';

export default {
  components: {
    Spinner,
    AuthActionButton,
  },
  data() {
    return {
      isPasswordChangingNow: false,
      isRequestSend: false,
      ssoOnlyMessage: '',
      inputData: {
        password: '',
      },
      isEmptyInput: '',
      activeInputName: null,
    };
  },
  created() {
    this.signature = this.$route.params.signature;
  },
  methods: {
    async handleForm() {
      hideErrors(['passwordError', 'passwordLengthError']);
      if (!this.inputData.password) {
        return raiseError('password', 'passwordError', 'new-password__input--length-error');
      }

      this.checkPasswordLength(this.inputData, 'new-password__input--length-error');

      this.checkFormInputsForErrors(this.inputData, 'new-password__input--error');
      if (
        this.checkFormInputsForCompleteness(this.inputData) &&
        validatePassword(this.inputData.password)
      ) {
        await this.sendRequest();
      }
    },
    async sendRequest() {
      this.ssoOnlyMessage = '';
      try {
        this.isPasswordChangingNow = true;
        await this.$auth.changePassword({ id: this.signature, password: this.inputData.password });
        this.isPasswordChangingNow = false;
        this.isRequestSend = true;
      } catch (error) {
        this.isPasswordChangingNow = false;
        const data = error?.response?.data;
        if (error?.response?.status === 403 && data?.code === 'SSO_ONLY') {
          this.ssoOnlyMessage = data?.errorMessage || ERROR_CODES.SSO_ONLY.body;
        } else {
          console.log(error);
        }
      }
    },
    checkKeyEvent(event) {
      this.activeInputName = event.target.id;
    },
    toggleShowPassword,
    checkFormInputsForErrors,
    checkFormInputsForCompleteness,
    checkPasswordLength,
    validatePassword,
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

.new-password {
  display: flex;
  align-items: center;
  flex-direction: column;

  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
    margin: 36px 0;
  }

  &__form {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 395px;
    margin-bottom: 40px;

    .icon {
      margin: 4px;
      width: 22px;
      height: 22px;
    }
  }

  &__message {
    width: 100%;
  }

  &__toggle-show-password {
    position: absolute;
    right: 11px;
    top: 5px;
    width: 30px;
    z-index: 5;
    cursor: pointer;
  }

  &__input {
    &--error,
    &--length-error {
      border: 1px solid #fb8d62;
      border-radius: 5px;
    }
  }

  &__text {
    margin: 0;
    @include Aspekta-font(500, 14px, 150%, #706969);

    &--center {
      text-align: center;
    }
  }

  &__link--orange {
    font-family: Cabin, serif;

    .nav-link {
      margin: 0;
    }

    a {
      font-weight: normal;
      font-size: 18px !important;
      color: #ffb400;
    }
  }

  &__link-to-login-wrapper {
    display: flex;
    align-items: center;
  }

  &__btn-submit {
    background: #ff7a08;
    border: 2px solid #ff7a08;
    box-sizing: border-box;
    border-radius: 8px;
    @include Aspekta-font(600, 18px, normal, #ffffff);
    color: #ffffff;
    padding: 10px 40px;
    margin: 0 0 28px 0;
    transition: 0.3s;

    &:focus {
      border: 2px solid #ffb400;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &__back-link {
    max-width: 195px;
    margin: 0 auto;

    a.nav-link {
      display: flex;
      justify-content: center;
      background: #ff7a08;
      border: 2px solid #ff7a08;
      box-sizing: border-box;
      border-radius: 8px;
      @include Aspekta-font(600, 18px, normal, #ffffff);
      color: #ffffff;
      margin-top: 30px;
      padding: 10px 0;
      max-width: 195px;
      transition: 0.3s;

      &:focus {
        border: 2px solid #ffb400;
      }

      &:hover {
        opacity: 0.7;
      }
    }
  }

  &__error {
    width: 100%;
    max-width: 831px;
    position: relative;
  }

  &__error-text {
    position: absolute;
    top: -30px;
    left: 3px;
    font-family: Cabin, serif;
    font-style: normal;
    font-weight: normal;
    font-size: 10px;
    line-height: 150%;
    color: #fb8d62;
    margin: 0;
  }

  &__sso-message {
    font-family: Cabin, serif;
    font-size: 16px;
    color: #fb8d62;
    margin: 1rem 0 0;
    text-align: center;
  }
}

#passwordError,
#passwordLengthError {
  display: none;
}

@media (max-width: 768px) {
  .new-password {
    padding: 0 20px;

    &__title {
      margin: 30px 0;
      text-align: center;
    }

    &__form {
      margin-bottom: 30px;
    }

    &__btn-submit {
      padding: 10px 0;
      width: 100%;
      margin: 15px 0 28px 0;
    }

    &__back-link {
      width: 100% !important;
      max-width: 100%;

      a.nav-link {
        max-width: 100%;
        width: 100% !important;
        font-size: 18px !important;
      }
    }
  }
}
</style>
